/**
 * Plays one ElevenLabs synthesis of `text` in `voiceId`, on demand.
 *
 * The clip is fetched once per (text, voiceId) pair and replayed from the same
 * blob on subsequent toggles; a change to either invalidates it so the next
 * play re-synthesizes rather than replaying stale audio.
 */
import { useEffect, useRef, useState } from 'react'

import { api } from '../lib/api'

export function useVoicePlayback(text: string, voiceId: string | undefined, runId?: string) {
  const [isLoading, setIsLoading] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const reset = () => {
    audioRef.current?.pause()
    audioRef.current = null
    setIsPlaying(false)
  }

  useEffect(() => {
    reset()
    return reset
    // Only text/voiceId identity should invalidate the cached clip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, voiceId])

  const toggle = async () => {
    if (isPlaying && audioRef.current) {
      audioRef.current.pause()
      setIsPlaying(false)
      return
    }

    if (audioRef.current) {
      void audioRef.current.play()
      setIsPlaying(true)
      return
    }

    if (!voiceId) return

    setIsLoading(true)
    setError(null)
    try {
      const blob = await api.fetchTtsBlob(text, voiceId, runId)
      const url = URL.createObjectURL(blob)
      const newAudio = new Audio(url)
      newAudio.onended = () => setIsPlaying(false)
      newAudio.onerror = () => {
        setIsPlaying(false)
        setError('Failed to play audio')
      }
      audioRef.current = newAudio
      await newAudio.play()
      setIsPlaying(true)
    } catch (err) {
      setError((err as Error).message || 'Failed to synthesize speech')
    } finally {
      setIsLoading(false)
    }
  }

  return { isLoading, isPlaying, error, toggle }
}
